import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Icon, type IconName } from '../components/Icon.tsx';
import { QrCode } from '../components/Receipt.tsx';
import { Shell } from '../components/Shell.tsx';
import { Button, Dialog, EmptyState, Field, Skeleton, StatusBadge, Switch, type Tone } from '../components/ui.tsx';
import { t, tn, type MessageKey } from '../i18n/index.ts';
import { action } from '../lib/api.ts';
import { dateTime, day, elapsed, money, parseMoney } from '../lib/format.ts';
import { useImageUrl } from '../lib/menu.ts';
import { CourierDialog } from './Shipping.tsx';
import { useSession } from '../lib/session.tsx';
import type { OrderView } from '../lib/types.ts';
import { useUi } from '../lib/ui-state.tsx';
import { TONES, useAction, useSave } from './manage/common.tsx';
import { PayDialog } from './Pay.tsx';
import { CartsTab } from './ShopCarts.tsx';
import { Bars, downloadCsv, Kpi, Table } from './Reports.tsx';

/**
 * The online shop for the staff: orders coming in from the web shop (confirm, pack, send, take
 * the payment), the products sold online and how they are shown, how orders reach customers
 * and are paid, and the results.
 */

type State = 'new' | 'confirmed' | 'ready' | 'shipped' | 'done' | 'cancelled';
type Filter = 'active' | 'new' | 'done' | 'cancelled' | 'all';
type Tab = 'overview' | 'orders' | 'carts' | 'products' | 'settings' | 'reports';

interface ShopOrder {
  id: string;
  number: number;
  state: State;
  location: string;
  customer: string | null;
  name: string;
  phone: string;
  email: string | null;
  delivery: 'pickup' | 'delivery';
  address: string | null;
  city: string | null;
  payment: 'cod' | 'bank' | 'card';
  note: string | null;
  items: { product: string; name: string; qty: number; unit_price: number; total: number }[];
  subtotal: number;
  delivery_fee: number;
  total: number;
  order: string | null;
  tracking: string | null;
  paid_in: boolean;
  at: string;
  confirmed_at: string | null;
  ready_at: string | null;
  shipped_at: string | null;
  done_at: string | null;
  cancelled_at: string | null;
  cancel_reason: string | null;
  by_customer: boolean;
}
interface Detail extends ShopOrder {
  link: string;
  location_name: string;
  confirmed_by_name: string | null;
  bill: OrderView | null;
}
interface Overview {
  enabled: boolean;
  paused: boolean;
  tenant: string;
  listed: number;
  counts: { new: number; confirmed: number; ready: number; shipped: number };
  week: { orders: number; value: number };
  month: { orders: number; value: number };
  series: { day: string; orders: number; value: number }[];
  top: { product: string; name: string; qty: number; value: number }[];
  todo: ShopOrder[];
  today: string;
}
interface Settings {
  id: string | null;
  enabled: boolean;
  paused: boolean;
  title: string | null;
  tagline: string | null;
  about: string | null;
  message: string | null;
  accent: (typeof TONES)[number];
  location: string | null;
  pickup: boolean;
  delivery: boolean;
  delivery_product: string | null;
  delivery_min: number;
  free_over: number;
  delivery_area: string | null;
  delivery_days: number;
  cod: boolean;
  bank: boolean;
  bank_text: string | null;
  card: boolean;
  terms: string | null;
  phone: string | null;
  email: string | null;
}
interface Setup {
  settings: Settings;
  tenant: string;
  locations: { id: string; name: string }[];
  products: { id: string; name: string; price: number }[];
  /** Card payments in the Payments module: null when it is not in the plan. */
  card: { ready: boolean; test: boolean } | null;
}
interface ProductRow {
  id: string;
  name: string;
  price: number;
  image: string | null;
  available: boolean;
  category: string | null;
  category_name: string | null;
  product_description: string | null;
  listing: string | null;
  listed: boolean;
  featured: boolean | null;
  sequence: number | null;
  description: string | null;
  max_per_order: number | null;
  stock: number | null;
  sold_30: number;
}
interface Report {
  from: string;
  to: string;
  orders: number;
  cancelled: number;
  cancelled_by_customer: number;
  done: number;
  value: number;
  collected: number;
  delivery: number;
  customers: number;
  average: number;
  confirm_minutes: number | null;
  products: { product: string; name: string; qty: number; value: number; orders: number }[];
  ways: { delivery: 'pickup' | 'delivery'; payment: 'cod' | 'bank' | 'card'; orders: number; value: number }[];
  reasons: { reason: string | null; n: number }[];
}

const TABS: { key: Tab; icon: IconName; permission?: string }[] = [
  { key: 'overview', icon: 'chart' },
  { key: 'orders', icon: 'receipt' },
  { key: 'carts', icon: 'cart' },
  { key: 'products', icon: 'box', permission: 'shop.manage' },
  { key: 'settings', icon: 'settings', permission: 'shop.manage' },
  { key: 'reports', icon: 'trendUp' },
];

const STATE: Record<State, { tone: Tone; icon: IconName }> = {
  new: { tone: 'warn', icon: 'bell' },
  confirmed: { tone: 'info', icon: 'box' },
  ready: { tone: 'primary', icon: 'checkCircle' },
  shipped: { tone: 'primary', icon: 'truck' },
  done: { tone: 'ok', icon: 'check' },
  cancelled: { tone: 'neutral', icon: 'x' },
};
const stateLabel = (o: Pick<ShopOrder, 'state' | 'delivery'>) =>
  o.state === 'ready' && o.delivery === 'delivery' ? t('shop.st_packed') : o.state === 'done' && o.delivery === 'delivery' ? t('shop.st_delivered') : t(`shop.st_${o.state}` as MessageKey);
function StateBadge({ o }: { o: Pick<ShopOrder, 'state' | 'delivery'> }) {
  const s = STATE[o.state];
  return <StatusBadge tone={s.tone} icon={s.icon} label={stateLabel(o)} pulse={o.state === 'new'} />;
}

export const shopUrl = (tenant: string) => `${window.location.origin}/shop/${tenant}`;

export function Shop() {
  const [params, setParams] = useSearchParams();
  const { can } = useSession();
  const tabs = TABS.filter((x) => !x.permission || can(x.permission));
  const tab = (tabs.some((x) => x.key === params.get('tab')) ? params.get('tab') : 'overview') as Tab;
  const { data: o, reload } = useAction<Overview>('shop.overview');
  const [version, setVersion] = useState(0);
  const open = params.get('order');
  const set = (next: Record<string, string | null>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) (v === null ? p.delete(k) : p.set(k, v));
    setParams(p, { replace: true });
  };
  const changed = async () => {
    setVersion((v) => v + 1);
    await reload();
  };
  // New orders arrive while the page is open: look again every half minute.
  useEffect(() => {
    const id = setInterval(() => {
      if (!document.hidden) void reload();
    }, 30_000);
    return () => clearInterval(id);
  }, [reload]);
  const show = (id: string | null) => set({ order: id });

  return (
    <Shell>
      <div className="page reports shop-admin">
        <div className="page-head">
          <div>
            <h1>{t('shop.title')}</h1>
            <p className="muted">{t('shop.subtitle')}</p>
          </div>
          {o?.enabled ? (
            <div className="page-actions">
              <a className="btn btn-secondary btn-md" href={shopUrl(o.tenant)} target="_blank" rel="noreferrer">
                <Icon name="link" size={20} />
                <span className="btn-label">{t('shop.open_shop')}</span>
              </a>
            </div>
          ) : null}
        </div>
        {!o ? (
          <Skeleton h={110} r="var(--radius-lg)" />
        ) : (
          <>
            {!o.enabled ? (
              <p className="callout callout-warn">
                <Icon name="alert" size={16} />
                <span>{can('shop.manage') ? t('shop.off_manager') : t('shop.off')}</span>
                {can('shop.manage') ? (
                  <Button size="sm" onClick={() => set({ tab: 'settings' })}>
                    {t('shop.set_up')}
                  </Button>
                ) : null}
              </p>
            ) : o.paused ? (
              <p className="callout callout-warn">
                <Icon name="pause" size={16} />
                <span>{t('shop.paused_note')}</span>
              </p>
            ) : null}
            <section className="rep-kpis" aria-label={t('shop.at_a_glance')}>
              <Kpi label={t('shop.k_new')} value={String(o.counts.new)} hint={o.counts.new ? t('shop.k_new_hint') : t('shop.k_none_waiting')} warn={o.counts.new > 0} />
              <Kpi label={t('shop.k_to_send')} value={String(o.counts.confirmed + o.counts.ready)} hint={tn('shop.k_on_the_way', o.counts.shipped)} />
              <Kpi label={t('shop.k_week')} value={money(o.week.value)} hint={tn('shop.k_orders', o.week.orders)} />
              <Kpi label={t('shop.k_month')} value={money(o.month.value)} hint={o.month.orders ? t('shop.k_average', { amount: money(Math.round(o.month.value / Math.max(1, o.month.orders))) }) : tn('shop.k_orders', 0)} />
            </section>
          </>
        )}

        <div className="segmented tabs shop-tabs" role="tablist" aria-label={t('shop.sections')}>
          {tabs.map((x) => (
            <button key={x.key} role="tab" aria-selected={tab === x.key} className={tab === x.key ? 'active' : ''} onClick={() => set({ tab: x.key === 'overview' ? null : x.key, f: null, q: null })}>
              <Icon name={x.icon} size={16} />
              {t(`shop.tab_${x.key}` as MessageKey)}
              {x.key === 'orders' && o && o.counts.new ? <span className="count">{o.counts.new}</span> : null}
            </button>
          ))}
        </div>

        {!o ? null : tab === 'overview' ? (
          <OverviewTab o={o} onOpen={show} onAll={() => set({ tab: 'orders' })} />
        ) : tab === 'orders' ? (
          <OrdersTab key={version} params={params} set={set} onOpen={show} />
        ) : tab === 'carts' ? (
          <CartsTab />
        ) : tab === 'products' ? (
          <ProductsTab tenant={o.tenant} />
        ) : tab === 'settings' ? (
          <SettingsTab onSaved={() => void reload()} />
        ) : (
          <ReportsTab today={o.today} />
        )}
      </div>
      {open ? <OrderDialog key={open} id={open} onClose={() => show(null)} onChange={changed} /> : null}
    </Shell>
  );
}

// ---- overview ---------------------------------------------------------------------

function OverviewTab({ o, onOpen, onAll }: { o: Overview; onOpen: (id: string) => void; onAll: () => void }) {
  return (
    <div className="shop-overview">
      <section className="panel">
        <div className="prot-history-head">
          <div>
            <h2 className="rep-sub">{t('shop.to_do')}</h2>
            <p className="muted small">{t('shop.to_do_hint')}</p>
          </div>
          <Button size="sm" icon="chevronRight" onClick={onAll}>
            {t('shop.all_orders')}
          </Button>
        </div>
        {o.todo.length ? (
          <ul className="shop-todo">
            {o.todo.map((x) => (
              <li key={x.id}>
                <button className="shop-todo-row" onClick={() => onOpen(x.id)}>
                  <span className={`shop-todo-icon tone-${STATE[x.state].tone}`}>
                    <Icon name={x.delivery === 'delivery' ? 'truck' : 'store'} size={18} />
                  </span>
                  <span className="shop-todo-main">
                    <strong>
                      WEB-{x.number} · {x.name}
                    </strong>
                    <span className="muted">
                      {tn('shop.n_items', x.items.reduce((s, i) => s + i.qty, 0))} · {x.delivery === 'delivery' ? [x.city].filter(Boolean).join('') || t('shop.delivery') : t('shop.pickup')} · {elapsed(x.at)}
                    </span>
                  </span>
                  <span className="shop-todo-end">
                    <strong className="tabular">{money(x.total)}</strong>
                    <StateBadge o={x} />
                  </span>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState icon="checkCircle" title={t('shop.nothing_to_do')} body={t('shop.nothing_to_do_hint')} />
        )}
      </section>
      <div className="shop-side">
        <section className="panel">
          <h2 className="rep-sub">{t('shop.last_30')}</h2>
          <Bars
            small
            items={o.series.map((d) => ({
              key: d.day,
              label: day(d.day).slice(0, 5),
              value: d.value,
              tip: `${day(d.day)} · ${money(d.value)} · ${tn('shop.k_orders', d.orders)}`,
            }))}
          />
        </section>
        <section className="panel">
          <h2 className="rep-sub">{t('shop.best_sellers')}</h2>
          {o.top.length ? (
            <ol className="shop-top-list">
              {o.top.map((x, i) => (
                <li key={x.product}>
                  <span className="shop-rank">{i + 1}</span>
                  <span className="shop-top-name">{x.name}</span>
                  <span className="muted tabular">× {Math.round(x.qty)}</span>
                  <strong className="tabular">{money(x.value)}</strong>
                </li>
              ))}
            </ol>
          ) : (
            <p className="muted">{t('shop.no_sales_yet')}</p>
          )}
        </section>
      </div>
    </div>
  );
}

// ---- orders -----------------------------------------------------------------------

const FILTERS: Filter[] = ['active', 'new', 'done', 'cancelled', 'all'];

function OrdersTab({ params, set, onOpen }: { params: URLSearchParams; set: (n: Record<string, string | null>) => void; onOpen: (id: string) => void }) {
  const filter = (FILTERS.includes(params.get('f') as Filter) ? params.get('f') : 'active') as Filter;
  const [search, setSearch] = useState(params.get('q') ?? '');
  const [q, setQ] = useState(search);
  useEffect(() => {
    const id = setTimeout(() => setQ(search.trim()), 300);
    return () => clearTimeout(id);
  }, [search]);
  const { data } = useAction<ShopOrder[]>('shop.orders', { filter, ...(q ? { search: q } : {}) });
  return (
    <section className="panel">
      <div className="toolbar">
        <div className="segmented subtle shop-filters" role="radiogroup" aria-label={t('shop.show')}>
          {FILTERS.map((f) => (
            <button key={f} role="radio" aria-checked={filter === f} className={filter === f ? 'active' : ''} onClick={() => set({ f: f === 'active' ? null : f })}>
              {t(`shop.f_${f}` as MessageKey)}
            </button>
          ))}
        </div>
        <label className="search">
          <Icon name="search" size={16} />
          <input className="input" type="search" value={search} placeholder={t('shop.search')} aria-label={t('shop.search')} onChange={(e) => setSearch(e.target.value)} />
        </label>
      </div>
      {!data ? (
        <Skeleton h={240} />
      ) : (
        <Table
          view="shop.orders"
          head={[t('shop.c_order'), t('shop.c_customer'), t('shop.c_how'), t('shop.c_items'), t('shop.c_total'), t('shop.c_state')]}
          phoneHide={[0, 2, 3]}
          left={[1, 2]}
          empty={filter === 'active' ? t('shop.none_active') : t('shop.none_found')}
          rows={data.map((x) => ({
            key: x.id,
            onClick: () => onOpen(x.id),
            cells: [
              <span className="vat-party" key="n">
                <strong>WEB-{x.number}</strong>
                <span className="muted">{dateTime(x.at)}</span>
              </span>,
              <span className="vat-party" key="c">
                <span>{x.name}</span>
                <span className="muted">
                  <span className="shop-phone-only">WEB-{x.number} · </span>
                  {x.phone}
                </span>
              </span>,
              <span key="h" className="shop-how">
                <Icon name={x.delivery === 'delivery' ? 'truck' : 'store'} size={16} />
                {x.delivery === 'delivery' ? x.city || t('shop.delivery') : t('shop.pickup')}
                {x.payment === 'bank' || x.payment === 'card' ? (
                  <span
                    className={`shop-bank-chip${x.paid_in ? ' paid' : ''}`}
                    title={x.payment === 'card' ? (x.paid_in ? t('shop.card_in') : t('shop.card_waiting')) : x.paid_in ? t('shop.bank_in') : t('shop.bank_waiting')}
                  >
                    <Icon name={x.payment === 'card' ? 'card' : 'bank'} size={12} />
                  </span>
                ) : null}
              </span>,
              <span className="tabular" key="i">
                {x.items.reduce((s, i) => s + i.qty, 0)}
              </span>,
              <span className="tabular" key="t">
                {money(x.total)}
              </span>,
              <StateBadge key="s" o={x} />,
            ],
          }))}
        />
      )}
    </section>
  );
}

function OrderDialog({ id, onClose, onChange }: { id: string; onClose: () => void; onChange: () => Promise<void> }) {
  const { can } = useSession();
  const { busy, run } = useSave();
  const { data: o, reload } = useAction<Detail>('shop.order', { id });
  const [ask, setAsk] = useState<null | 'cancel' | 'ship' | { pay: OrderView }>(null);
  const [reason, setReason] = useState('');
  const [tracking, setTracking] = useState('');
  const [trackingUrl, setTrackingUrl] = useState<string | null>(null);
  const [courier, setCourier] = useState(false);
  const [tried, setTried] = useState(false);
  const handle = can('shop.handle');
  const done = async () => {
    setAsk(null);
    await reload();
    await onChange();
  };
  const act = (name: string, input: Record<string, unknown>, msg: string) =>
    void run(async () => {
      await action(name, { id, ...input });
      await done();
    }, msg);
  if (!o) {
    return (
      <Dialog title={t('shop.order')} onClose={onClose} size="lg">
        <Skeleton h={320} />
      </Dialog>
    );
  }
  const open = ['confirmed', 'ready', 'shipped'].includes(o.state);
  const waitingBank = o.payment === 'bank' && !o.paid_in;
  const timeline: { label: string; at: string | null }[] = [
    { label: t('shop.tl_received'), at: o.at },
    { label: o.confirmed_by_name ? t('shop.tl_confirmed_by', { name: o.confirmed_by_name }) : t('shop.tl_confirmed'), at: o.confirmed_at },
    { label: o.delivery === 'delivery' ? t('shop.tl_packed') : t('shop.tl_ready'), at: o.ready_at },
    ...(o.delivery === 'delivery' ? [{ label: t('shop.tl_shipped'), at: o.shipped_at }] : []),
    { label: o.delivery === 'delivery' ? t('shop.tl_delivered') : t('shop.tl_collected'), at: o.done_at },
  ];
  return (
    <>
      <Dialog
        title={`WEB-${o.number} · ${o.name}`}
        onClose={onClose}
        size="lg"
        footer={
          handle && (o.state === 'new' || open) ? (
            <>
              <Button variant="ghost" icon="x" onClick={() => setAsk('cancel')}>
                {t('shop.cancel')}
              </Button>
              {o.state === 'new' ? (
                <Button variant="primary" icon="check" busy={busy} onClick={() => act('shop.confirm', {}, t('shop.confirmed_msg', { n: o.number }))}>
                  {t('shop.confirm')}
                </Button>
              ) : null}
              {o.state === 'confirmed' ? (
                <Button icon="box" busy={busy} onClick={() => act('shop.ready', {}, o.delivery === 'delivery' ? t('shop.packed_msg') : t('shop.ready_msg'))}>
                  {o.delivery === 'delivery' ? t('shop.mark_packed') : t('shop.mark_ready')}
                </Button>
              ) : null}
              {o.delivery === 'delivery' && (o.state === 'confirmed' || o.state === 'ready') ? (
                <Button variant={o.state === 'ready' ? 'primary' : 'secondary'} icon="truck" disabled={waitingBank} onClick={() => setAsk('ship')}>
                  {t('shop.send')}
                </Button>
              ) : null}
              {open && o.bill && can('pos.pay') && (o.delivery === 'pickup' ? o.state === 'ready' || o.state === 'confirmed' : o.state === 'shipped') ? (
                <Button variant="primary" icon="cash" onClick={() => setAsk({ pay: o.bill! })}>
                  {o.delivery === 'delivery' ? t('shop.delivered_pay') : t('shop.collected_pay')}
                </Button>
              ) : null}
            </>
          ) : undefined
        }
      >
        <div className="form-stack">
          <div className="item-summary">
            <div>
              <span className="muted">{t('shop.c_total')}</span>
              <strong className="tabular">{money(o.total)}</strong>
            </div>
            <div>
              <span className="muted">{t('shop.c_how')}</span>
              <strong>{o.delivery === 'delivery' ? t('shop.delivery') : t('shop.pickup')}</strong>
            </div>
            <div>
              <span className="muted">{t('shop.c_payment')}</span>
              <strong>{o.payment === 'card' ? t('shop.pay_card') : o.payment === 'bank' ? t('shop.pay_bank') : o.delivery === 'delivery' ? t('shop.pay_cod') : t('shop.pay_at_pickup')}</strong>
            </div>
            <StateBadge o={o} />
          </div>

          {o.state === 'new' ? (
            <p className="callout callout-info">
              <Icon name="bell" size={16} />
              <span>{t('shop.new_note')}</span>
            </p>
          ) : null}
          {o.payment === 'card' && o.state !== 'cancelled' && o.state !== 'done' ? (
            <div className={`callout ${o.paid_in ? 'callout-ok' : 'callout-warn'} shop-bank-row`}>
              <Icon name={o.paid_in ? 'checkCircle' : 'card'} size={16} />
              <span>{o.paid_in ? t('shop.card_in_long') : t('shop.card_waiting_long', { amount: money(o.total) })}</span>
            </div>
          ) : null}
          {o.payment === 'bank' && o.state !== 'cancelled' && o.state !== 'done' ? (
            <div className={`callout ${o.paid_in ? 'callout-ok' : 'callout-warn'} shop-bank-row`}>
              <Icon name={o.paid_in ? 'checkCircle' : 'bank'} size={16} />
              <span>{o.paid_in ? t('shop.bank_in') : t('shop.bank_waiting_long', { amount: money(o.total), n: o.number })}</span>
              {handle ? (
                <Button size="sm" busy={busy} onClick={() => act('shop.paid_in', { paid: !o.paid_in }, o.paid_in ? t('shop.bank_undone') : t('shop.bank_marked'))}>
                  {o.paid_in ? t('shop.bank_undo') : t('shop.bank_mark')}
                </Button>
              ) : null}
            </div>
          ) : null}
          {o.state === 'cancelled' ? (
            <p className="callout">
              <Icon name="x" size={16} />
              <span>{[o.by_customer ? t('shop.cancelled_by_customer') : t('shop.cancelled_note'), o.by_customer ? null : o.cancel_reason].filter(Boolean).join(' · ')}</span>
            </p>
          ) : null}

          <div className="shop-detail">
            <section>
              <h3 className="rep-sub">{t('shop.items')}</h3>
              <ShopItems o={o} />
            </section>
            <section>
              <h3 className="rep-sub">{t('shop.customer')}</h3>
              <dl className="appr-facts">
                <dt>{t('shop.c_customer')}</dt>
                <dd>{o.name}</dd>
                <dt>{t('shop.phone')}</dt>
                <dd>
                  <a href={`tel:${o.phone.replace(/\s/g, '')}`}>{o.phone}</a>
                </dd>
                {o.email ? (
                  <>
                    <dt>{t('shop.email')}</dt>
                    <dd>
                      <a href={`mailto:${o.email}`}>{o.email}</a>
                    </dd>
                  </>
                ) : null}
                <dt>{o.delivery === 'delivery' ? t('shop.deliver_to') : t('shop.pick_up_at')}</dt>
                <dd>{o.delivery === 'delivery' ? [o.address, o.city].filter(Boolean).join(', ') : o.location_name}</dd>
                {o.note ? (
                  <>
                    <dt>{t('shop.note')}</dt>
                    <dd>“{o.note}”</dd>
                  </>
                ) : null}
                {o.tracking ? (
                  <>
                    <dt>{t('shop.tracking')}</dt>
                    <dd>{o.tracking}</dd>
                  </>
                ) : null}
                {o.bill ? (
                  <>
                    <dt>{t('shop.bill')}</dt>
                    <dd>
                      {t('shop.bill_no', { n: o.bill.order.number })}
                      {o.bill.order.receipt_no ? ` · ${t('shop.receipt_no', { n: o.bill.order.receipt_no })}` : ''}
                    </dd>
                  </>
                ) : null}
              </dl>
              <h3 className="rep-sub">{t('shop.history')}</h3>
              <ol className="shop-timeline">
                {timeline.map((x) => (
                  <li key={x.label} className={x.at ? 'done' : ''}>
                    <span className="shop-tl-dot" aria-hidden="true" />
                    <span>{x.label}</span>
                    <span className="muted tabular">{x.at ? dateTime(x.at) : '—'}</span>
                  </li>
                ))}
              </ol>
              <a className="link small" href={o.link} target="_blank" rel="noreferrer">
                <Icon name="link" size={14} /> {t('shop.customer_page')}
              </a>
            </section>
          </div>
        </div>
      </Dialog>
      {ask === 'cancel' ? (
        <Dialog
          title={t('shop.cancel_title', { n: o.number })}
          onClose={() => setAsk(null)}
          size="sm"
          footer={
            <Button
              variant="danger"
              icon="x"
              busy={busy}
              onClick={() => {
                setTried(true);
                if (reason.trim().length >= 3) act('shop.cancel', { reason: reason.trim() }, t('shop.cancelled_msg'));
              }}
            >
              {t('shop.cancel')}
            </Button>
          }
        >
          <p className="muted">{o.bill ? t('shop.cancel_bill_note') : t('shop.cancel_note')}</p>
          {o.payment === 'card' && o.paid_in ? (
            <p className="callout callout-info">
              <Icon name="undo" size={16} />
              <span>{t('shop.cancel_card_refund', { amount: money(o.total) })}</span>
            </p>
          ) : null}
          <Field label={t('shop.reason')} hint={t('shop.reason_hint')} error={tried && reason.trim().length < 3 ? t('shop.reason_needed') : null}>
            <input className="input" value={reason} maxLength={200} onChange={(e) => setReason(e.target.value)} />
          </Field>
        </Dialog>
      ) : null}
      {ask === 'ship' ? (
        <Dialog
          title={t('shop.send_title', { n: o.number })}
          onClose={() => setAsk(null)}
          size="sm"
          footer={
            <Button variant="primary" icon="truck" busy={busy} onClick={() => act('shop.ship', { tracking: tracking.trim() || undefined, ...(tracking.trim() && trackingUrl ? { tracking_url: trackingUrl } : {}) }, t('shop.sent_msg'))}>
              {t('shop.send')}
            </Button>
          }
        >
          <p className="muted">{[o.address, o.city].filter(Boolean).join(', ')}</p>
          {can('shipping.ship') ? (
            <div className="sh-shop">
              <Button icon="truck" onClick={() => setCourier(true)}>
                {t('sh.buy_title')}
              </Button>
              <span className="muted small">{t('sh.shop_hint')}</span>
            </div>
          ) : null}
          <Field label={t('shop.tracking')} hint={t('shop.tracking_hint')}>
            <input
              className="input"
              value={tracking}
              maxLength={80}
              onChange={(e) => {
                setTracking(e.target.value);
                setTrackingUrl(null);
              }}
            />
          </Field>
          {courier ? (
            <CourierDialog
              label={t('sh.shop_label', { n: o.number })}
              purpose={{ module: 'shop', record: o.id }}
              to={{ name: o.name, phone: o.phone, email: o.email, street1: o.address ?? '', city: o.city ?? '' }}
              items={o.items.map((i) => ({ name: i.name, qty: i.qty, total: i.total }))}
              onClose={() => setCourier(false)}
              onBought={(b) => {
                setTracking(b.tracking_code ?? '');
                setTrackingUrl(b.public_url);
              }}
            />
          ) : null}
        </Dialog>
      ) : null}
      {ask && typeof ask === 'object' ? (
        <PayDialog
          view={ask.pay}
          onClose={() => void done()}
          pay={(input) => action<OrderView & { change: number }>('shop.complete', { id, ...input })}
          next={{ label: t('shop.pay_done'), icon: 'check' }}
          onPaid={() => void done()}
        />
      ) : null}
    </>
  );
}

function ShopItems({ o }: { o: ShopOrder }) {
  return (
    <table className="shop-items">
      <tbody>
        {o.items.map((i) => (
          <tr key={i.product}>
            <td className="tabular">{i.qty} ×</td>
            <td>{i.name}</td>
            <td className="tabular muted">{money(i.unit_price)}</td>
            <td className="tabular">{money(i.total)}</td>
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr>
          <td colSpan={3}>{t('shop.subtotal')}</td>
          <td className="tabular">{money(o.subtotal)}</td>
        </tr>
        {o.delivery === 'delivery' ? (
          <tr>
            <td colSpan={3}>{t('shop.delivery')}</td>
            <td className="tabular">{o.delivery_fee ? money(o.delivery_fee) : t('shop.free')}</td>
          </tr>
        ) : null}
        <tr className="grand">
          <td colSpan={3}>{t('shop.c_total')}</td>
          <td className="tabular">{money(o.total)}</td>
        </tr>
      </tfoot>
    </table>
  );
}

// ---- products -----------------------------------------------------------------------

function ProductsTab({ tenant }: { tenant: string }) {
  const { data, reload } = useAction<ProductRow[]>('shop.products');
  const { fail, toast } = useUi();
  const [show, setShow] = useState<'listed' | 'other'>('listed');
  const [search, setSearch] = useState('');
  const [edit, setEdit] = useState<ProductRow | null>(null);
  const imageUrl = useImageUrl();
  const q = search.trim().toLowerCase();
  const rows = (data ?? []).filter((p) => (show === 'listed' ? p.listed : !p.listed)).filter((p) => !q || p.name.toLowerCase().includes(q) || (p.category_name ?? '').toLowerCase().includes(q));
  const listed = (data ?? []).filter((p) => p.listed).length;
  const toggle = async (p: ProductRow, active: boolean) => {
    try {
      await action('shop.save_listing', {
        product: p.id,
        active,
        featured: p.featured ?? false,
        sequence: p.sequence ?? listed * 10,
        description: p.description,
        max_per_order: p.max_per_order ?? 0,
      });
      toast(active ? t('shop.listed_msg', { name: p.name }) : t('shop.unlisted_msg', { name: p.name }));
      await reload();
    } catch (err) {
      fail(err);
    }
  };
  return (
    <section className="panel">
      <div className="prot-history-head">
        <div>
          <h2 className="rep-sub">{t('shop.products_title')}</h2>
          <p className="muted small">{t('shop.products_hint')}</p>
        </div>
        <a className="btn btn-ghost btn-sm" href={shopUrl(tenant)} target="_blank" rel="noreferrer">
          <Icon name="eye" size={16} />
          <span className="btn-label">{t('shop.preview')}</span>
        </a>
      </div>
      <div className="toolbar">
        <div className="segmented subtle" role="radiogroup" aria-label={t('shop.show')}>
          <button role="radio" aria-checked={show === 'listed'} className={show === 'listed' ? 'active' : ''} onClick={() => setShow('listed')}>
            {t('shop.p_listed')} <span className="count">{listed}</span>
          </button>
          <button role="radio" aria-checked={show === 'other'} className={show === 'other' ? 'active' : ''} onClick={() => setShow('other')}>
            {t('shop.p_other')}
          </button>
        </div>
        <label className="search">
          <Icon name="search" size={16} />
          <input className="input" type="search" value={search} placeholder={t('shop.search_products')} aria-label={t('shop.search_products')} onChange={(e) => setSearch(e.target.value)} />
        </label>
      </div>
      {!data ? (
        <Skeleton h={240} />
      ) : rows.length ? (
        <ul className="shop-plist">
          {rows.map((p) => {
            const src = imageUrl(p);
            return (
              <li key={p.id} className="shop-prow">
                {src ? (
                  <img className="shop-prow-photo" src={src} alt="" loading="lazy" />
                ) : (
                  <span className="shop-prow-photo shop-nophoto" aria-hidden="true">
                    <Icon name="box" size={20} />
                  </span>
                )}
                <div className="shop-prow-main">
                  <strong>
                    {p.name}
                    {p.featured && p.listed ? (
                      <span className="shop-star" title={t('shop.featured')}>
                        <Icon name="star" size={14} />
                      </span>
                    ) : null}
                  </strong>
                  <span className="muted">
                    {[p.category_name, p.stock === null ? null : p.stock === 0 ? t('shop.out_of_stock') : tn('shop.in_stock', p.stock), p.sold_30 ? tn('shop.sold_30', Math.round(p.sold_30)) : null]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                  {p.listed && !(p.description || p.product_description) ? <span className="warn-text small">{t('shop.no_description')}</span> : null}
                </div>
                <strong className="tabular">{money(p.price)}</strong>
                {p.listed ? (
                  <Button size="sm" icon="edit" onClick={() => setEdit(p)}>
                    {t('shop.edit')}
                  </Button>
                ) : null}
                <Switch checked={p.listed} onChange={(v) => void toggle(p, v)} label={p.listed ? t('shop.on_sale_named', { name: p.name }) : t('shop.put_on_sale', { name: p.name })} />
              </li>
            );
          })}
        </ul>
      ) : (
        <EmptyState icon="box" title={show === 'listed' ? t('shop.none_listed') : t('shop.none_other')} body={show === 'listed' ? t('shop.none_listed_hint') : undefined} />
      )}
      {edit ? (
        <ListingDialog
          p={edit}
          onClose={() => setEdit(null)}
          onSaved={async () => {
            setEdit(null);
            await reload();
          }}
        />
      ) : null}
    </section>
  );
}

function ListingDialog({ p, onClose, onSaved }: { p: ProductRow; onClose: () => void; onSaved: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [description, setDescription] = useState(p.description ?? '');
  const [featured, setFeatured] = useState(!!p.featured);
  const [sequence, setSequence] = useState(String(p.sequence ?? 0));
  const [max, setMax] = useState(p.max_per_order ? String(p.max_per_order) : '');
  const save = () =>
    void run(async () => {
      await action('shop.save_listing', {
        product: p.id,
        active: true,
        featured,
        sequence: Number(sequence) || 0,
        description: description.trim() || null,
        max_per_order: Number(max) || 0,
      });
      await onSaved();
    }, t('shop.saved'));
  return (
    <Dialog
      title={p.name}
      onClose={onClose}
      footer={
        <Button variant="primary" icon="check" busy={busy} onClick={save}>
          {t('common.save')}
        </Button>
      }
    >
      <div className="form-stack">
        <div className="setting-row">
          <div>
            <strong>{t('shop.featured')}</strong>
            <p className="muted">{t('shop.featured_hint')}</p>
          </div>
          <Switch checked={featured} onChange={setFeatured} label={t('shop.featured')} />
        </div>
        <Field label={t('shop.description')} hint={p.product_description ? t('shop.description_hint_has') : t('shop.description_hint')} counter={`${description.length}/4000`}>
          <textarea className="input" rows={5} value={description} maxLength={4000} placeholder={p.product_description ?? ''} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        <div className="form-grid">
          <Field label={t('shop.sequence')} hint={t('shop.sequence_hint')}>
            <input className="input" inputMode="numeric" value={sequence} onChange={(e) => setSequence(e.target.value.replace(/\D/g, ''))} />
          </Field>
          <Field label={t('shop.max_per_order')} hint={t('shop.max_per_order_hint')}>
            <input className="input" inputMode="numeric" value={max} placeholder="—" onChange={(e) => setMax(e.target.value.replace(/\D/g, ''))} />
          </Field>
        </div>
        <p className="muted small">{t('shop.photo_hint')}</p>
      </div>
    </Dialog>
  );
}

// ---- settings -------------------------------------------------------------------------

function SettingsTab({ onSaved }: { onSaved: () => void }) {
  const { data, reload } = useAction<Setup>('shop.setup');
  if (!data) return <Skeleton h={480} r="var(--radius-lg)" />;
  return (
    <ShopSettings
      setup={data}
      onSaved={() => {
        void reload();
        onSaved();
      }}
    />
  );
}

const lek = (minor: number) => (minor ? String(minor / 100) : '');

function ShopSettings({ setup, onSaved }: { setup: Setup; onSaved: () => void }) {
  const { busy, run } = useSave();
  const [s, setS] = useState<Settings>(setup.settings);
  const [minText, setMinText] = useState(lek(setup.settings.delivery_min));
  const [freeText, setFreeText] = useState(lek(setup.settings.free_over));
  const [tried, setTried] = useState(false);
  const set = <K extends keyof Settings>(k: K, v: Settings[K]) => setS((x) => ({ ...x, [k]: v }));
  const fee = setup.products.find((p) => p.id === s.delivery_product);
  const min = minText.trim() ? parseMoney(minText) : 0;
  const free = freeText.trim() ? parseMoney(freeText) : 0;
  const errors = {
    ways: !s.pickup && !s.delivery ? t('shop.e_ways') : null,
    pays: !s.cod && !s.bank && !s.card ? t('shop.e_pays') : null,
    product: s.delivery && !s.delivery_product ? t('shop.e_fee') : null,
    min: min === null ? t('shop.e_amount') : null,
    free: free === null ? t('shop.e_amount') : null,
    bank: s.bank && !s.bank_text?.trim() ? t('shop.e_bank') : null,
    email: s.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.email.trim()) ? t('shop.e_email') : null,
  };
  const invalid = Object.values(errors).some(Boolean);
  const save = () => {
    setTried(true);
    if (invalid) return;
    void run(async () => {
      const { id: _id, ...rest } = s;
      await action('shop.save_settings', { ...rest, delivery_min: min ?? 0, free_over: free ?? 0, email: s.email?.trim() || null });
      onSaved();
    }, t('shop.saved'));
  };
  const err = (e: string | null) => (tried ? e : null);
  const options = useMemo(() => [...setup.products].sort((a, b) => a.name.localeCompare(b.name)), [setup.products]);

  return (
    <div className="online-settings">
      <section className="card">
        <header className="card-head">
          <div>
            <h2>
              <Icon name="cart" size={20} /> {t('shop.s_shop')}
            </h2>
            <span className="muted">{t('shop.s_shop_hint')}</span>
          </div>
          <Switch checked={s.enabled} onChange={(v) => set('enabled', v)} label={t('shop.s_open')} />
        </header>
        <div className="setting-row">
          <div>
            <strong>{t('shop.s_pause')}</strong>
            <p className="muted">{t('shop.s_pause_hint')}</p>
          </div>
          <Switch checked={s.paused} onChange={(v) => set('paused', v)} label={t('shop.s_pause')} />
        </div>
        <div className="form-grid">
          <Field label={t('shop.s_title')} hint={t('shop.s_title_hint')}>
            <input className="input" value={s.title ?? ''} maxLength={80} onChange={(e) => set('title', e.target.value)} />
          </Field>
          <Field label={t('shop.s_tagline')} hint={t('shop.s_tagline_hint')}>
            <input className="input" value={s.tagline ?? ''} maxLength={160} onChange={(e) => set('tagline', e.target.value)} />
          </Field>
          <Field label={t('shop.s_message')} hint={t('shop.s_message_hint')}>
            <input className="input" value={s.message ?? ''} maxLength={200} onChange={(e) => set('message', e.target.value)} />
          </Field>
          <Field label={t('shop.s_phone')}>
            <input className="input" type="tel" value={s.phone ?? ''} maxLength={40} onChange={(e) => set('phone', e.target.value)} />
          </Field>
          <Field label={t('shop.s_email')} error={err(errors.email)}>
            <input className="input" type="email" value={s.email ?? ''} maxLength={120} onChange={(e) => set('email', e.target.value)} />
          </Field>
          {setup.locations.length > 1 ? (
            <Field label={t('shop.s_location')} hint={t('shop.s_location_hint')}>
              <select className="input" value={s.location ?? ''} onChange={(e) => set('location', e.target.value || null)}>
                <option value="">{t('shop.s_main_location')}</option>
                {setup.locations.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                  </option>
                ))}
              </select>
            </Field>
          ) : null}
        </div>
        <Field label={t('shop.s_about')} hint={t('shop.s_about_hint')} counter={`${(s.about ?? '').length}/2000`}>
          <textarea className="input" rows={3} value={s.about ?? ''} maxLength={2000} onChange={(e) => set('about', e.target.value)} />
        </Field>
        <fieldset className="option-group">
          <legend>{t('shop.s_colour')}</legend>
          <div className="tone-picker" role="radiogroup" aria-label={t('shop.s_colour')}>
            {TONES.map((tone) => (
              <button key={tone} role="radio" aria-checked={s.accent === tone} className={`tone-dot tone-${tone}${s.accent === tone ? ' active' : ''}`} onClick={() => set('accent', tone)} aria-label={t(`tone.${tone}` as MessageKey)} title={t(`tone.${tone}` as MessageKey)}>
                {s.accent === tone ? <Icon name="check" size={16} /> : null}
              </button>
            ))}
          </div>
        </fieldset>
        {setup.settings.enabled ? <ShareLink tenant={setup.tenant} /> : null}
      </section>

      <section className="card">
        <header className="card-head">
          <div>
            <h2>
              <Icon name="truck" size={20} /> {t('shop.s_ways')}
            </h2>
            <span className="muted">{t('shop.s_ways_hint')}</span>
          </div>
        </header>
        <div className="setting-row">
          <div>
            <strong>{t('shop.s_pickup')}</strong>
            <p className="muted">{t('shop.s_pickup_hint')}</p>
          </div>
          <Switch checked={s.pickup} onChange={(v) => set('pickup', v)} label={t('shop.s_pickup')} />
        </div>
        <div className="setting-row">
          <div>
            <strong>{t('shop.s_delivery')}</strong>
            <p className="muted">{t('shop.s_delivery_hint')}</p>
          </div>
          <Switch checked={s.delivery} onChange={(v) => set('delivery', v)} label={t('shop.s_delivery')} />
        </div>
        {err(errors.ways) ? (
          <p className="field-msg" role="alert">
            <Icon name="alert" size={14} /> {errors.ways}
          </p>
        ) : null}
        {s.delivery ? (
          <div className="form-grid">
            <Field label={t('shop.s_fee')} hint={fee ? t('shop.s_fee_is', { price: money(fee.price) }) : t('shop.s_fee_hint')} error={err(errors.product)}>
              <select className="input" value={s.delivery_product ?? ''} onChange={(e) => set('delivery_product', e.target.value || null)}>
                <option value="">{t('shop.s_choose')}</option>
                {options.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} · {money(p.price)}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={t('shop.s_free_over')} hint={t('shop.s_free_over_hint')} error={err(errors.free)}>
              <input className="input" inputMode="decimal" value={freeText} placeholder="—" onChange={(e) => setFreeText(e.target.value)} />
            </Field>
            <Field label={t('shop.s_min')} hint={t('shop.s_min_hint')} error={err(errors.min)}>
              <input className="input" inputMode="decimal" value={minText} placeholder="0" onChange={(e) => setMinText(e.target.value)} />
            </Field>
            <Field label={t('shop.s_days')} hint={t('shop.s_days_hint')}>
              <input className="input" inputMode="numeric" value={s.delivery_days || ''} placeholder="0" onChange={(e) => set('delivery_days', Math.min(60, Number(e.target.value.replace(/\D/g, '')) || 0))} />
            </Field>
            <Field label={t('shop.s_area')} hint={t('shop.s_area_hint')}>
              <input className="input" value={s.delivery_area ?? ''} maxLength={160} onChange={(e) => set('delivery_area', e.target.value)} />
            </Field>
          </div>
        ) : null}
      </section>

      <section className="card">
        <header className="card-head">
          <div>
            <h2>
              <Icon name="cash" size={20} /> {t('shop.s_pays')}
            </h2>
            <span className="muted">{t('shop.s_pays_hint')}</span>
          </div>
        </header>
        <div className="setting-row">
          <div>
            <strong>{t('shop.s_cod')}</strong>
            <p className="muted">{t('shop.s_cod_hint')}</p>
          </div>
          <Switch checked={s.cod} onChange={(v) => set('cod', v)} label={t('shop.s_cod')} />
        </div>
        <div className="setting-row">
          <div>
            <strong>{t('shop.s_bank')}</strong>
            <p className="muted">{t('shop.s_bank_hint')}</p>
          </div>
          <Switch checked={s.bank} onChange={(v) => set('bank', v)} label={t('shop.s_bank')} />
        </div>
        {err(errors.pays) ? (
          <p className="field-msg" role="alert">
            <Icon name="alert" size={14} /> {errors.pays}
          </p>
        ) : null}
        {s.bank ? (
          <Field label={t('shop.s_bank_text')} hint={t('shop.s_bank_text_hint')} error={err(errors.bank)}>
            <textarea className="input" rows={3} value={s.bank_text ?? ''} maxLength={1000} onChange={(e) => set('bank_text', e.target.value)} />
          </Field>
        ) : null}
        {setup.card ? (
          <div className="setting-row">
            <div>
              <strong>{t('shop.s_card')}</strong>
              <p className="muted">
                {setup.card.ready ? (setup.card.test ? t('shop.s_card_hint_test') : t('shop.s_card_hint')) : t('shop.s_card_not_ready')}{' '}
                {!setup.card.ready ? <a href="/payments?tab=settings">{t('shop.s_card_set_up')}</a> : null}
              </p>
            </div>
            <Switch checked={s.card} disabled={!setup.card.ready && !s.card} onChange={(v) => set('card', v)} label={t('shop.s_card')} />
          </div>
        ) : (
          <p className="callout callout-info">
            <Icon name="shieldCheck" size={16} />
            <span>{t('shop.s_no_cards')}</span>
          </p>
        )}
      </section>

      <section className="card">
        <header className="card-head">
          <div>
            <h2>
              <Icon name="book" size={20} /> {t('shop.s_terms')}
            </h2>
            <span className="muted">{t('shop.s_terms_hint')}</span>
          </div>
        </header>
        <Field label={t('shop.s_terms_text')} counter={`${(s.terms ?? '').length}/10000`}>
          <textarea className="input" rows={6} value={s.terms ?? ''} maxLength={10000} onChange={(e) => set('terms', e.target.value)} />
        </Field>
      </section>

      <div className="form-actions">
        <Button variant="primary" icon="check" busy={busy} onClick={save}>
          {t('common.save')}
        </Button>
      </div>
    </div>
  );
}

function ShareLink({ tenant }: { tenant: string }) {
  const { toast } = useUi();
  const url = shopUrl(tenant);
  const [qr, setQr] = useState<string[] | null>(null);
  useEffect(() => {
    void action<string[]>('shop.qr', { text: url }).then(setQr, () => setQr(null));
  }, [url]);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast(t('shop.copied'));
    } catch {
      window.prompt(t('onlineset.copy_by_hand'), url);
    }
  };
  return (
    <div className="online-share">
      {qr ? <QrCode rows={qr} label={t('onlineset.qr_for', { url })} /> : <Skeleton h={120} w={120} />}
      <div className="online-share-text">
        <strong>{t('shop.share_title')}</strong>
        <p className="muted">{t('shop.share_hint')}</p>
        <code className="online-url">{url}</code>
        <div className="online-share-actions">
          <Button size="sm" icon="copy" onClick={() => void copy()}>
            {t('onlineset.copy')}
          </Button>
          <a className="btn btn-ghost btn-sm" href={url} target="_blank" rel="noreferrer">
            <Icon name="link" size={16} />
            <span className="btn-label">{t('onlineset.open')}</span>
          </a>
        </div>
      </div>
    </div>
  );
}

// ---- reports -------------------------------------------------------------------------

function addDaysIso(d: string, n: number) {
  const x = new Date(`${d}T00:00:00Z`);
  x.setUTCDate(x.getUTCDate() + n);
  return x.toISOString().slice(0, 10);
}

function ReportsTab({ today }: { today: string }) {
  const [from, setFrom] = useState(`${today.slice(0, 8)}01`);
  const [to, setTo] = useState(today);
  const { data } = useAction<Report>('shop.report', { from, to });
  const presets: { key: string; label: string; from: string; to: string }[] = [
    { key: 'month', label: t('shop.r_this_month'), from: `${today.slice(0, 8)}01`, to: today },
    { key: '30', label: t('shop.r_30_days'), from: addDaysIso(today, -29), to: today },
    { key: '90', label: t('shop.r_90_days'), from: addDaysIso(today, -89), to: today },
  ];
  const csv = () => {
    if (!data) return;
    downloadCsv(
      `shop-products-${from}-${to}.csv`,
      [t('shop.c_product'), t('shop.c_qty'), t('shop.c_orders'), t('shop.c_value')],
      data.products.map((p) => [p.name, p.qty, p.orders, (p.value / 100).toFixed(2)]),
    );
  };
  const wayLabel = (w: Report['ways'][number]) =>
    `${w.delivery === 'delivery' ? t('shop.delivery') : t('shop.pickup')} · ${w.payment === 'card' ? t('shop.pay_card') : w.payment === 'bank' ? t('shop.pay_bank') : t('shop.pay_on_receipt')}`;
  return (
    <div className="form-stack">
      <section className="panel">
        <div className="toolbar">
          <div className="segmented subtle" role="radiogroup" aria-label={t('shop.r_period')}>
            {presets.map((p) => (
              <button key={p.key} role="radio" aria-checked={from === p.from && to === p.to} className={from === p.from && to === p.to ? 'active' : ''} onClick={() => (setFrom(p.from), setTo(p.to))}>
                {p.label}
              </button>
            ))}
          </div>
          <div className="shop-range">
            <input className="input" type="date" value={from} max={to} aria-label={t('shop.r_from')} onChange={(e) => e.target.value && setFrom(e.target.value)} />
            <span className="muted">–</span>
            <input className="input" type="date" value={to} min={from} aria-label={t('shop.r_to')} onChange={(e) => e.target.value && setTo(e.target.value)} />
          </div>
        </div>
        {!data ? (
          <Skeleton h={110} />
        ) : (
          <section className="rep-kpis" aria-label={t('shop.r_summary')}>
            <Kpi label={t('shop.r_value')} value={money(data.value)} hint={t('shop.r_collected', { amount: money(data.collected) })} />
            <Kpi label={t('shop.r_orders')} value={String(data.orders - data.cancelled)} hint={t('shop.k_average', { amount: money(data.average) })} />
            <Kpi label={t('shop.r_customers')} value={String(data.customers)} hint={data.confirm_minutes !== null ? t('shop.r_confirm_speed', { n: data.confirm_minutes }) : undefined} />
            <Kpi
              label={t('shop.r_cancelled')}
              value={String(data.cancelled)}
              hint={data.orders ? t('shop.r_cancel_rate', { pct: Math.round((data.cancelled / data.orders) * 100), mine: data.cancelled_by_customer }) : undefined}
              warn={data.orders > 0 && data.cancelled / data.orders > 0.15}
            />
          </section>
        )}
      </section>
      {data ? (
        <div className="shop-overview">
          <section className="panel">
            <div className="prot-history-head">
              <h2 className="rep-sub">{t('shop.r_by_product')}</h2>
              <Button size="sm" icon="download" disabled={!data.products.length} onClick={csv}>
                {t('shop.csv')}
              </Button>
            </div>
            <Table
              head={[t('shop.c_product'), t('shop.c_qty'), t('shop.c_orders'), t('shop.c_value')]}
              phoneHide={[2]}
              empty={t('shop.r_none')}
              rows={data.products.map((p) => ({
                key: p.product,
                cells: [p.name, <span key="q" className="tabular">{Math.round(p.qty * 100) / 100}</span>, <span key="o" className="tabular">{p.orders}</span>, <span key="v" className="tabular">{money(p.value)}</span>],
              }))}
            />
          </section>
          <div className="shop-side">
            <section className="panel">
              <h2 className="rep-sub">{t('shop.r_by_way')}</h2>
              {data.ways.length ? (
                <ul className="shop-ways">
                  {data.ways.map((w) => (
                    <li key={`${w.delivery}-${w.payment}`}>
                      <span>
                        <Icon name={w.delivery === 'delivery' ? 'truck' : 'store'} size={16} /> {wayLabel(w)}
                      </span>
                      <span className="muted tabular">{tn('shop.k_orders', w.orders)}</span>
                      <strong className="tabular">{money(w.value)}</strong>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="muted">{t('shop.r_none')}</p>
              )}
              {data.delivery ? <p className="muted small">{t('shop.r_delivery_fees', { amount: money(data.delivery) })}</p> : null}
            </section>
            <section className="panel">
              <h2 className="rep-sub">{t('shop.r_reasons')}</h2>
              {data.reasons.length ? (
                <ul className="shop-ways">
                  {data.reasons.map((r) => (
                    <li key={r.reason ?? '-'}>
                      <span>{r.reason === 'Cancelled by the customer' ? t('shop.cancelled_by_customer') : (r.reason ?? '—')}</span>
                      <strong className="tabular">{r.n}</strong>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="muted">{t('shop.r_no_cancel')}</p>
              )}
            </section>
          </div>
        </div>
      ) : null}
    </div>
  );
}
